import { useState, useEffect } from 'react';
import { format, subDays } from 'date-fns';
import { History, Download, Search, User, Activity, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { auditAPI } from '../lib/api';
import { toast } from 'sonner';

const actionColors = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  approve: 'bg-emerald-100 text-emerald-800',
  reject: 'bg-orange-100 text-orange-800',
  submit: 'bg-purple-100 text-purple-800',
  login: 'bg-cyan-100 text-cyan-800',
  error_logged: 'bg-red-100 text-red-800',
};

const moduleIcons = {
  travel: '✈️',
  timesheet: '⏱️',
  expense: '💰',
  payroll: '💵',
  leave: '🏖️',
  system: '⚙️',
  user: '👤',
};

export default function AuditTrailPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  
  // Filters
  const [fromDate, setFromDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [toDate, setToDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [module, setModule] = useState('all');
  const [action, setAction] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        from_date: fromDate,
        to_date: toDate,
        module: module !== 'all' ? module : undefined,
        action: action !== 'all' ? action : undefined,
        search: search || undefined,
        page,
        limit: 50,
      };
      const { data } = await auditAPI.getLogs(params);
      setLogs(data.logs);
      setTotal(data.total);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await auditAPI.getStats('week');
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [fromDate, toDate, module, action, page]);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleSearch = () => {
    setPage(1);
    fetchLogs();
  };

  const handleExport = async () => {
    try {
      const { data } = await auditAPI.exportLogs({
        from_date: fromDate,
        to_date: toDate,
        module: module !== 'all' ? module : undefined,
        action: action !== 'all' ? action : undefined,
      });
      
      // Download CSV
      const blob = new Blob([data.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.total_rows} records`);
    } catch (error) {
      toast.error('Failed to export audit logs');
    }
  };

  const viewLogDetail = async (log) => {
    try {
      const { data } = await auditAPI.getLog(log.id);
      setSelectedLog(data);
      setSheetOpen(true);
    } catch (error) {
      toast.error('Failed to load log details');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <History className="h-6 w-6" />
            System Audit Trail
          </h1>
          <p className="text-muted-foreground">Track all critical actions in the system</p>
        </div>
        <Button onClick={handleExport}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.total_events}</div>
              <p className="text-sm text-muted-foreground">Total Events (7 days)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{Object.keys(stats.by_module).length}</div>
              <p className="text-sm text-muted-foreground">Active Modules</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.top_actors?.length || 0}</div>
              <p className="text-sm text-muted-foreground">Active Users</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-600">{stats.critical_events?.length || 0}</div>
              <p className="text-sm text-muted-foreground">Critical Events</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-40"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-40"
              />
            </div>
            <Select value={module} onValueChange={setModule}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Module" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                <SelectItem value="travel">Travel</SelectItem>
                <SelectItem value="timesheet">Timesheet</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
                <SelectItem value="leave">Leave</SelectItem>
                <SelectItem value="payroll">Payroll</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="approve">Approve</SelectItem>
                <SelectItem value="reject">Reject</SelectItem>
                <SelectItem value="submit">Submit</SelectItem>
                <SelectItem value="login">Login</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex-1 flex gap-2">
              <Input
                placeholder="Search by name, entity..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="max-w-xs"
              />
              <Button variant="outline" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-muted-foreground">
              <Activity className="h-16 w-16 mb-4 text-muted-foreground/30" />
              <p className="text-lg">No audit logs found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="p-3 font-medium">Timestamp</th>
                      <th className="p-3 font-medium">User</th>
                      <th className="p-3 font-medium">Module</th>
                      <th className="p-3 font-medium">Action</th>
                      <th className="p-3 font-medium">Entity</th>
                      <th className="p-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-muted/50">
                        <td className="p-3 text-sm">
                          {format(new Date(log.timestamp), 'MMM d, HH:mm')}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{log.actor_name || 'System'}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-sm">
                            {moduleIcons[log.module] || '📋'} {log.module}
                          </span>
                        </td>
                        <td className="p-3">
                          <Badge className={actionColors[log.action] || 'bg-gray-100'}>
                            {log.action}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">
                          {log.entity_title || log.entity_id}
                        </td>
                        <td className="p-3">
                          <Button variant="ghost" size="sm" onClick={() => viewLogDetail(log)}>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {logs.length} of {total} records
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={logs.length < 50}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-[500px] sm:max-w-[500px]">
          <SheetHeader>
            <SheetTitle>Audit Event Detail</SheetTitle>
          </SheetHeader>
          {selectedLog && (
            <ScrollArea className="h-[calc(100vh-100px)] mt-4">
              <div className="space-y-6 pr-4">
                <div>
                  <p className="text-xs text-muted-foreground">Event ID</p>
                  <p className="font-mono text-sm">{selectedLog.log.id}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Timestamp</p>
                  <p>{format(new Date(selectedLog.log.timestamp), 'PPpp')}</p>
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Actor</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-muted-foreground">User:</span> {selectedLog.log.actor_name} ({selectedLog.log.actor_email})</p>
                    <p><span className="text-muted-foreground">Role:</span> {selectedLog.log.actor_role}</p>
                    {selectedLog.log.ip_address && (
                      <p><span className="text-muted-foreground">IP:</span> {selectedLog.log.ip_address}</p>
                    )}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Action</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-muted-foreground">Module:</span> {selectedLog.log.module}</p>
                    <p><span className="text-muted-foreground">Action:</span> <Badge className={actionColors[selectedLog.log.action]}>{selectedLog.log.action}</Badge></p>
                    <p><span className="text-muted-foreground">Entity:</span> {selectedLog.log.entity_title || selectedLog.log.entity_id}</p>
                  </div>
                </div>

                {(selectedLog.log.before_state || selectedLog.log.after_state) && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Changes</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Before</p>
                        <pre className="bg-muted p-2 rounded text-xs overflow-auto max-h-40">
                          {JSON.stringify(selectedLog.log.before_state, null, 2) || 'N/A'}
                        </pre>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">After</p>
                        <pre className="bg-muted p-2 rounded text-xs overflow-auto max-h-40">
                          {JSON.stringify(selectedLog.log.after_state, null, 2) || 'N/A'}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {selectedLog.log.remarks && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Remarks</h4>
                    <p className="text-sm bg-muted p-3 rounded">{selectedLog.log.remarks}</p>
                  </div>
                )}

                {selectedLog.related_logs?.length > 0 && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Related Events</h4>
                    <div className="space-y-2">
                      {selectedLog.related_logs.map((rel) => (
                        <div key={rel.id} className="text-sm p-2 bg-muted/50 rounded">
                          <span className="font-medium">{rel.action}</span> by {rel.actor_name}
                          <span className="text-muted-foreground ml-2">
                            {format(new Date(rel.timestamp), 'MMM d, HH:mm')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
